import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export interface RpPaymentGatewayConfig {
  id: number;
  tenantId: string;
  merchantId: string;
  type: 'SEND' | 'RECEIVE';
  paymentMethod: string;
  paymentsApi: string;
  accessKey: string | null;
  secretKey: string | null;
  // Location-level toggle: route this location's ACH through payments-api.
  usePaymentsApi: boolean;
  status: 'ACTIVE' | 'INACTIVE';
  createdAt: string;
  modifiedAt: string;
}

export function useRpPaymentGatewaysConfig(tenantId: string, merchantId?: string) {
  return useQuery({
    queryKey: ['admin', 'rp-payment-gateways', tenantId, merchantId],
    queryFn: async () => {
      const res = await api.get<{ data: RpPaymentGatewayConfig[] }>(
        `/admin/rp-payment-gateways/${tenantId}/${merchantId}`,
      );
      return res;
    },
    enabled: !!tenantId && !!merchantId,
  });
}

export function useSaveRpPaymentGatewayConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      tenantId: string;
      merchantId: string;
      type: 'SEND' | 'RECEIVE';
      paymentMethod: string;
      paymentsApi: string;
      accessKey?: string;
      secretKey?: string;
      usePaymentsApi?: boolean;
    }) => api.post<{ data: RpPaymentGatewayConfig }>('/admin/rp-payment-gateways', payload),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'rp-payment-gateways', variables.tenantId, variables.merchantId],
      });
    },
  });
}

/**
 * Flips the payments-api routing flag on a location's ACTIVE ACH gateway for one
 * direction. Separate from the full save because it is a single switch the super
 * admin throws without re-entering credentials.
 */
export function useSetPaymentsApiRouting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      merchantId,
      type,
      usePaymentsApi,
    }: {
      tenantId: string;
      merchantId: string;
      type: 'SEND' | 'RECEIVE';
      usePaymentsApi: boolean;
    }) =>
      api.patch<{ data: RpPaymentGatewayConfig }>(
        `/admin/rp-payment-gateways/${tenantId}/${merchantId}/use-payments-api`,
        { type, usePaymentsApi },
      ),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'rp-payment-gateways', variables.tenantId, variables.merchantId],
      });
    },
  });
}
